import React from "react";
import { ArrowRight, Smartphone, Gift, CalendarHeart, CheckCircle2, Instagram } from "lucide-react";

// eslint-disable-next-line no-unused-vars
const primaryBg = { backgroundColor: "#1E3A8A", color: "white" };
const accentText = { color: "#2563EB" };

export default function Home() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "sans-serif", backgroundColor: "white", color: "#111827" }}>
      <Header />
      <main style={{ flex: 1 }}>
        <Hero />
        <Features />
        <Pricing />
        <CTA />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
}

function Header() {
  const isLoggedIn = !!sessionStorage.getItem('token');

  return (
    <header style={{ width: "100%", borderBottom: "1px solid #E5E7EB", position: "sticky", top: 0, backgroundColor: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)", zIndex: 50 }}>
      <div style={{ maxWidth: "1024px", margin: "0 auto", padding: "0 1rem", height: "4.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="#" style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={require('../assets/left_logo.png')}
            alt="Invite Avenue Logo"
            style={{ height: '8rem', objectFit: 'contain', marginLeft: '-3rem'}}
          />
        </a>
        <nav style={{ display: "flex", gap: "1.5rem", fontSize: "1rem" }}>
          <a href="#features" style={{ textDecoration: "none", color: "#374151" }}>Features</a>
          <a href="#pricing" style={{ textDecoration: "none", color: "#374151" }}>Pricing</a>
          <a href="#faq" style={{ textDecoration: "none", color: "#374151" }}>FAQ</a>
        </nav>
        <a href={isLoggedIn ? "/invites" : "/login"} style={{ ...primaryBg, padding: "0.5rem 1rem", borderRadius: "9999px", fontSize: "0.95rem", display: "inline-flex", alignItems: "center", gap: "0.35rem", textDecoration: "none" }}>
          {isLoggedIn ? "My Invites" : "Get started"} <ArrowRight size={16} />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section style={{ position: "relative", padding: "5rem 0" }}>
      <div style={{ maxWidth: "1024px", margin: "0 auto", padding: "0 1rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", alignItems: "center" }}>
          <div style={{ textAlign: "left" }}>
            <h1 style={{ fontSize: "3rem", fontWeight: "bold", color: "#111827", lineHeight: 1.2 }}>
              Beautiful Event Invitations <br /> Made for Mobile
            </h1>
            <p style={{ marginTop: "1.25rem", maxWidth: "28rem", color: "#4B5563", fontSize: "1.125rem", lineHeight: 1.6 }}>
              Invite Avenue helps you design, send, and track stunning online invitations perfectly optimized for phones.
            </p>
            <div style={{ marginTop: "2rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a href="/login" style={{ ...primaryBg, padding: "0.75rem 1.5rem", borderRadius: "9999px", fontSize: "1rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                Try it Free <ArrowRight size={18} />
              </a>
              {/* <a href="#features" style={{ ...accentText, fontSize: "0.875rem", textDecoration: "underline" }}>See how it works</a> */}
            </div>
          </div>
          <PhoneFrame src="https://landing-page-template.inviteavenue.com" />
        </div>
      </div>
    </section>
  );
}

function PhoneFrame({ src }) {
  const FRAME_W = 240, FRAME_H = 500, BORDER = 10;
  const DEVICE_W = 390, DEVICE_H = 844; // tweak to emulate another phone
  const vw = FRAME_W - BORDER * 2, vh = FRAME_H - BORDER * 2;
  const scale = Math.min(vw / DEVICE_W, vh / DEVICE_H);

  return (
    <div style={{ margin: "0 auto", width: FRAME_W, height: FRAME_H, position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, border: `${BORDER}px solid #111827`, borderRadius: "2.5rem", boxShadow: "0 4px 10px rgba(0,0,0,.3)", background: "#000", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: "6rem", height: "1.5rem", background: "#111827", borderBottomLeftRadius: "1rem", borderBottomRightRadius: "1rem", zIndex: 10 }} />
        <div style={{ width: DEVICE_W, height: DEVICE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <iframe title="Preview" src={src} scrolling="no" style={{ width: "100%", height: "100%", border: "none", background: "#fff" }} />
        </div>
      </div>
      <div style={{ position: "absolute", bottom: "0.75rem", left: "50%", transform: "translateX(-50%)", width: "5rem", height: "0.375rem", background: "#D1D5DB", borderRadius: "1rem" }} />
    </div>
  );
}

function Features() {
  const items = [
    { icon: <Smartphone size={28} style={accentText} />, title: "Mobile-first Design", desc: "Invitations that look stunning on any phone screen." },
    { icon: <Gift size={28} style={accentText} />, title: "Custom Modules", desc: "Use building blocks to create invitations that are uniquely yours." },
    { icon: <CalendarHeart size={28} style={accentText} />, title: "RSVP Tracking", desc: "Manage responses in real-time with a simple dashboard." },
    // { icon: <Languages size={28} style={accentText} />, title: "Multi-language Support", desc: "Create invitations in multiple languages to suit your guests." },
  ];
  return (
    <section id="features" style={{ padding: "4rem 0" }}>
      <div style={{ maxWidth: "1024px", margin: "0 auto", padding: "0 1rem" }}>
        <h2 style={{ fontSize: "2rem", fontWeight: "600", textAlign: "center" }}>Why Invite Avenue?</h2>
        <div style={{ marginTop: "2.5rem", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem" }}>
          {items.map((f) => (
            <div key={f.title} style={{ border: "1px solid #E5E7EB", padding: "1.5rem", borderRadius: "0.75rem", backgroundColor: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <div style={{ marginBottom: "0.75rem" }}>{f.icon}</div>
              <h3 style={{ fontWeight: 600, marginBottom: "0.5rem", fontSize: "1.125rem" }}>{f.title}</h3>
              <p style={{ fontSize: "1rem", color: "#4B5563", lineHeight: 1.5 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const plans = [
    { name: "Create & Craft", price: "Free", description: "Design and refine your invitation. Pay only when you're ready to share.", priceNote: "No cost until you publish", features: ["Unlimited edits", "Preview mode", "All modules available for design"] },
    { name: "Standard Invite", price: "€40", description: "Publish your invitation and unlock RSVP functionality.", priceNote: "per invitation", features: ["Publish your invite", "Enable RSVP tracking", "Basic email support"]},
    { name: "Premium Invite", price: "€60", description: "Premium modules, and advanced features.", priceNote: "per invitation", features: ["Everything in Standard Invite", "Premium modules", "Multi-language support", "Priority support"] },
  ];
  return (
    <section id="pricing" style={{ padding: "4rem 0", backgroundColor: "#EFF6FF" }}>
      <div style={{ maxWidth: "1024px", margin: "0 auto", padding: "0 1rem" }}>
        <h2 style={{ fontSize: "2rem", fontWeight: 600, textAlign: "center" }}>Pricing</h2>
        <p style={{ textAlign: "center", color: "#4B5563", marginTop: "0.75rem", fontSize: "1.1rem" }}>Start free. Build your invite and pay only when you're ready to share.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.5rem", marginTop: "2.5rem" }}>
          {plans.map((p) => (
            <div key={p.name} style={{ border: p.highlighted ? "2px solid #1E3A8A" : "1px solid #E5E7EB", borderRadius: "0.75rem", padding: "1.5rem", backgroundColor: "white", boxShadow: p.highlighted ? "0 2px 8px rgba(0,0,0,0.1)" : "none" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 600 }}>{p.name}</h3>
              <p style={{ fontSize: "1rem", color: "#374151", marginTop: "0.5rem", lineHeight: 1.5 }}>{p.description}</p>
              <div style={{ marginTop: "1rem" }}>
                <span style={{ fontSize: "2rem", fontWeight: "bold" }}>{p.price}</span>
                {p.priceNote && <div style={{ fontSize: "0.9rem", color: "#6B7280", marginTop: "0.25rem" }}>{p.priceNote}</div>}
              </div>
              <ul style={{ marginTop: "1.25rem", paddingLeft: "0", fontSize: "0.95rem", color: "#4B5563", listStyle: "none" }}>
                {p.features.map((f) => (
                  <li key={f} style={{ marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <CheckCircle2 size={18} style={accentText} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="get-started" style={{ padding: "4rem 0", backgroundColor: "#F9FAFB", textAlign: "center" }}>
      <div style={{ maxWidth: "400px", margin: "0 auto", padding: "0 1rem" }}>
        <h2 style={{ fontSize: "2rem", fontWeight: 600 }}>Ready to Create Your First Invite?</h2>
        <p style={{ marginTop: "1rem", fontSize: "1.1rem", color: "#4B5563", lineHeight: 1.5 }}>Sign up now and design your first mobile invitation in minutes.</p>
        <div style={{ marginTop: "1.75rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
          <a href="/register" style={{ ...primaryBg, padding: "0.75rem 1.5rem", borderRadius: "9999px", fontSize: "1rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>Create an account <ArrowRight size={18} /></a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <p style={{ fontSize: "0.95rem", color: "#6B7280" }}>No credit card required</p>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const qas = [
    { q: "Can I send unlimited invitations?", a: "You can create unlimited drafts for free. Publish invites with the Standard or Premium Invite plan." },
    { q: "Do I create my own templates?", a: "Yes, you design your invitations using our modules and tools, no pre-made templates required." },
    { q: "Can I track RSVP responses?", a: "Yes, RSVP tracking is enabled when you purchase a Standard or Premium Invite plan." },
  ];
  return (
    <section id="faq" style={{ padding: "4rem 0" }}>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "0 1rem" }}>
        <h2 style={{ fontSize: "1.75rem", fontWeight: 600, textAlign: "center" }}>FAQ</h2>
        <div style={{ marginTop: "2rem", border: "1px solid #E5E7EB", borderRadius: "0.75rem" }}>
          {qas.map((qa, i) => (
            <details key={qa.q} style={{ padding: "1.25rem", borderBottom: i !== qas.length - 1 ? "1px solid #E5E7EB" : "none" }}>
              <summary style={{ cursor: "pointer", fontSize: "1.05rem", fontWeight: 500 }}>{qa.q}</summary>
              <p style={{ marginTop: "0.75rem", fontSize: "1rem", color: "#4B5563", lineHeight: 1.5 }}>{qa.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      style={{
        padding: "2rem 0",
        borderTop: "1px solid #E5E7EB",
        fontSize: "0.9rem",
        color: "#6B7280",
      }}
    >
      <div
        style={{
          maxWidth: "1024px",
          margin: "0 auto",
          padding: "0 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        {/* Left side */}
        <p style={{ margin: 0 }}>© {new Date().getFullYear()} Invite Avenue. All rights reserved.</p>

        {/* Middle Instagram */}
        <a
          href="https://instagram.com/invite_avenue"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "#2563EB",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
            margin: "0 auto",
          }}
        >
          <Instagram size={16} /> Instagram
        </a>

        {/* Right side */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" style={{ textDecoration: "none", color: "#374151" }}>Privacy</a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" style={{ textDecoration: "none", color: "#374151" }}>Terms</a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" style={{ textDecoration: "none", color: "#374151" }}>Contact</a>
        </div>
      </div>
    </footer>
  );
}

